import { loadStripe, Stripe } from '@stripe/stripe-js';
import { SubscriptionTier, SUBSCRIPTION_PLANS } from '../types/user';
import { supabase } from './supabase';

let stripePromise: Promise<Stripe | null> | null = null;

export class StripeService {
  private stripe: Stripe | null = null;

  async initialize(): Promise<Stripe | null> {
    if (!this.stripe) {
      // Lazy-load Stripe only when actually needed
      if (!stripePromise) {
        const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (!key) {
          console.warn('Stripe publishable key not configured');
          return null;
        }
        stripePromise = loadStripe(key);
      }
      this.stripe = await stripePromise;
    }
    return this.stripe;
  }

  async createCheckoutSession(
    tier: SubscriptionTier,
    userId: string,
    successUrl: string,
    cancelUrl: string
  ) {
    // Ensure Stripe is loaded
    await this.initialize();
    
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        tier,
        userId,
        successUrl,
        cancelUrl,
      },
    });

    if (error) {
      console.error('Error creating checkout session:', error);
      throw new Error('Failed to create checkout session');
    }

    if (!data?.url) {
      throw new Error('No checkout URL returned');
    }

    // Redirect to Stripe Checkout
    window.location.href = data.url;
  }

  async createBillingPortalSession(customerId: string, returnUrl: string) {
    // Ensure Stripe is loaded
    await this.initialize();
    
    const { data, error } = await supabase.functions.invoke('create-billing-portal-session', {
      body: {
        customerId,
        returnUrl,
      },
    });

    if (error) {
      console.error('Error creating billing portal session:', error);
      throw new Error('Failed to create billing portal session');
    }

    if (!data?.url) {
      throw new Error('No billing portal URL returned');
    }

    window.location.href = data.url;
  }

  getSubscriptionFeatures(tier: SubscriptionTier) {
    return SUBSCRIPTION_PLANS[tier];
  }

  formatPrice(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }
}

export const stripeService = new StripeService();
